<template>
  <div class="project-list">
    <header class="dashboard-header">
      <div class="header-content">
        <div class="logo-section">
          <div class="kpmg-logo" @click="navigateToHome">
            <img src="/kpmg-logo-blue.svg" alt="KPMG" class="kpmg-logo-img" />
          </div>
          <h1>Project List</h1>
        </div>
        <button @click="goBack" class="back-btn">← Back to Dashboard</button>
      </div>
    </header>

    <main class="main-content">
      <div class="container">
        <div class="projects-section">
          <div class="section-header">
            <h2>All Projects</h2>
            <p class="project-count">{{ projects.length }} project{{ projects.length !== 1 ? 's' : '' }} found</p>
          </div>

          <div v-if="projects.length === 0" class="empty-state">
            <div class="empty-icon">📁</div>
            <h3>No Projects Found</h3>
            <p>You haven't created any projects yet. Start by creating a new project assessment.</p>
            <button @click="createNewProject" class="create-project-btn">
              Create New Project
            </button>
          </div>

          <div v-else class="projects-table">
            <div class="table-header">
              <span>Project Name</span>
              <span>Sector</span>
              <span>Stage</span>
              <span>Last Updated</span>
              <span>Score</span>
              <span>Actions</span>
            </div>
            
            <div 
              v-for="project in projects" 
              :key="project.id" 
              class="table-row"
            >
              <span class="project-name">{{ project.name }}</span>
              <span class="sector">{{ project.sector }}</span>
              <span :class="['stage', project.stage.toLowerCase().replace(/\s+/g, '-')]">
                {{ project.stage }}
              </span>
              <span class="date">{{ project.lastUpdated }}</span>
              <span class="score">{{ project.score }}/100</span>
              <div class="actions">
                <button @click="editProject(project.id)" class="action-btn edit">Edit</button>
                <button @click="viewProject(project.id)" class="action-btn view">View</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
export default {
  name: 'ProjectList',
  data() {
    return {
      projects: [
        {
          id: 1,
          name: 'Metro Line Extension Project',
          sector: 'Transport',
          stage: 'Implementation',
          lastUpdated: '2024-01-15',
          score: 78,
          totalCost: 450,
          implementingAgency: 'Department of Transportation',
          timeline: 48,
          fundingSource: 'PPP',
          description: 'Comprehensive metro line extension to improve urban transportation infrastructure and reduce traffic congestion.'
        },
        {
          id: 2,
          name: 'Water Treatment Facility',
          sector: 'Water',
          stage: 'Feasibility',
          lastUpdated: '2024-01-10',
          score: 85,
          totalCost: 280,
          implementingAgency: 'Ministry of Water Resources',
          timeline: 36,
          fundingSource: 'Government Budget',
          description: 'Modern water treatment facility to provide clean drinking water to urban and rural communities.'
        },
        {
          id: 3,
          name: 'Solar Power Plant',
          sector: 'Energy',
          stage: 'Concept',
          lastUpdated: '2024-01-08',
          score: 72,
          totalCost: 320,
          implementingAgency: 'Ministry of Energy',
          timeline: 42,
          fundingSource: 'International Loan',
          description: 'Large-scale solar power generation facility to support renewable energy transition and reduce carbon emissions.'
        },
        {
          id: 4,
          name: 'Hospital Infrastructure',
          sector: 'Health',
          stage: 'Pre-feasibility',
          lastUpdated: '2024-01-05',
          score: 68,
          totalCost: 180,
          implementingAgency: 'Ministry of Health',
          timeline: 30,
          fundingSource: 'Grant',
          description: 'State-of-the-art hospital infrastructure to improve healthcare services and medical emergency response capabilities.'
        },
        {
          id: 5,
          name: 'Smart City Initiative',
          sector: 'Urban infrastructure',
          stage: 'Procurement',
          lastUpdated: '2024-01-03',
          score: 81,
          totalCost: 520,
          implementingAgency: 'Ministry of Urban Development',
          timeline: 60,
          fundingSource: 'PPP',
          description: 'Integrated smart city development with IoT infrastructure, digital governance, and sustainable urban planning.'
        }
      ]
    }
  },
  methods: {
    editProject(projectId) {
      // Navigate to project creation with edit mode
      this.$emit('navigate', 'edit-project', { projectId })
    },
    
    viewProject(projectId) {
      // Navigate to view project with random data generation
      this.$emit('navigate', 'view-project', { projectId, viewMode: true })
    },
    
    
    createNewProject() {
      this.$emit('navigate', 'create-project')
    },
    
    goBack() {
      this.$emit('navigate', 'dashboard')
    },
    
    navigateToHome() {
      this.$emit('navigate', 'landing')
    }
  }
}
</script>

<style scoped>
.project-list {
  min-height: 100vh;
  background: rgb(229, 229, 229);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.dashboard-header {
  background: white;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.kpmg-logo {
  background: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.kpmg-logo:hover {
  transform: scale(1.05);
}

.kpmg-logo-img {
  height: 32px;
  width: auto;
}

.logo-section h1 {
  font-size: 1.5rem;
  color: rgb(51, 51, 51);
  margin: 0;
  font-weight: 600;
}

.back-btn {
  background: transparent;
  color: rgb(0, 51, 141);
  border: 1px solid #00338d;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.back-btn:hover {
  background: rgb(0, 51, 141);
  color: white;
  transform: translateY(-1px);
}

.main-content {
  padding: 2rem 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.projects-section {
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 4px 15px rgba(0,0,0,0.08);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.section-header h2 {
  font-size: 2rem;
  color: rgb(51, 51, 51);
  margin: 0;
  font-weight: 600;
}

.project-count {
  color: rgb(102, 102, 102);
  font-size: 1rem;
  margin: 0;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  opacity: 0.6;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: rgb(51, 51, 51);
  margin-bottom: 1rem;
  font-weight: 600;
}

.empty-state p {
  color: rgb(102, 102, 102);
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.create-project-btn {
  background: rgb(0, 51, 141);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s;
}

.create-project-btn:hover {
  background: #002a75;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 51, 141, 0.3);
}

/* Projects Table */
.projects-table {
  overflow-x: auto;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 80px 120px;
  gap: 1rem;
  padding: 1rem;
  background: rgb(229, 229, 229);
  border-radius: 8px;
  font-weight: 600;
  color: rgb(102, 102, 102);
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 80px 120px;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  transition: background 0.3s;
}

.table-row:hover {
  background: rgb(229, 229, 229);
}

.project-name {
  font-weight: 600;
  color: rgb(51, 51, 51);
}

.sector {
  color: rgb(102, 102, 102);
}

.stage {
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  text-align: center;
  text-transform: capitalize;
}

.stage.implementation {
  background: #cce5ff;
  color: #004085;
}

.stage.feasibility {
  background: #d4edda;
  color: #155724;
}

.stage.concept {
  background: #fff3cd;
  color: #856404;
}

.stage.pre-feasibility {
  background: #e2e3e5;
  color: #383d41;
}

.stage.procurement {
  background: #f8d7da;
  color: #721c24;
}

.date {
  color: rgb(102, 102, 102);
  font-size: 0.9rem;
}

.score {
  font-weight: 600;
  color: rgb(0, 51, 141);
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  padding: 0.4rem 0.8rem;
  border: none;
  border-radius: 15px;
  cursor: pointer;
  font-size: 0.7rem;
  font-weight: 600;
  transition: all 0.3s;
}

.action-btn.edit {
  background: #fff3cd;
  color: #856404;
}

.action-btn.edit:hover {
  background: #ffeaa7;
  transform: translateY(-1px);
}

.action-btn.view {
  background: #cce5ff;
  color: #004085;
}

.action-btn.view:hover {
  background: #99d3ff;
  transform: translateY(-1px);
}

.action-btn.delete {
  background: #f8d7da;
  color: #721c24;
}

.action-btn.delete:hover {
  background: #f5c6cb;
  transform: translateY(-1px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
  
  .table-header span,
  .table-row span,
  .table-row div {
    text-align: left;
    padding: 0.25rem 0;
  }
  
  .actions {
    justify-content: flex-start;
  }
}
</style>